import { ControlledFormCheckbox } from '@components/formInputs/ControlledFormCheckbox';
import { ControlledFormSegmentedControl } from '@components/formInputs/ControlledFormSegmentedControl';
import { ControlledFormTextareaAutosize } from '@components/formInputs/ControlledFormTextareaAutosize';
import type { GuestEmailRequirement } from '@shared/types/restaurants';
import { type Control, useWatch } from 'react-hook-form';
import styles from './EventConfirmationStep.scss';
import type { EventFormData } from '../RestaurantEventForm';

export const MAX_CUSTOM_CONFIRMATION_MESSAGE_LENGTH = 480;

const GUEST_EMAIL_REQUIREMENT_OPTIONS: {
  label: string;
  value: GuestEmailRequirement;
}[] = [
  { label: 'Optional', value: 'optional' },
  { label: 'Required', value: 'required' },
  { label: 'Hidden', value: 'hidden' },
];

export const GUEST_EMAIL_REQUIREMENT_HELPER_TEXT: Record<
  GuestEmailRequirement,
  string
> = {
  hidden: 'Guests are not asked for an email address at checkout.',
  optional:
    'Email is requested as an optional field on checkout but not required.',
  required:
    'Email is required on checkout. Guests cannot complete their purchase without it.',
};

const GUEST_EMAIL_HELPER_ID = 'guest-email-requirement-helper';

export const EventConfirmationStep = ({
  control,
}: {
  control: Control<EventFormData>;
}) => {
  const guestEmailRequirement = useWatch({
    control,
    name: 'guestEmailRequirement',
  });

  return (
    <section className={styles.eventConfirmationStep}>
      <ControlledFormTextareaAutosize<EventFormData>
        control={control}
        name="customConfirmationMessage"
        label="Custom confirmation message"
        placeholder="Custom confirmation message"
        minRows={4}
        rules={{
          maxLength: {
            value: MAX_CUSTOM_CONFIRMATION_MESSAGE_LENGTH,
            message: `Your message must be ${MAX_CUSTOM_CONFIRMATION_MESSAGE_LENGTH} characters or less`,
          },
        }}
        labelClassName={styles.fieldLabel}
        ariaLabel="Custom confirmation message"
        showOptionalSuffix
        darkMode
      />
      <ControlledFormCheckbox<EventFormData>
        control={control}
        name="includeTicketsLinkInConfirmation"
        label="Include link to view tickets"
        className={styles.checkbox}
      />
      <p className={styles.description}>
        This message is sent to guests by text message (SMS) when their
        reservation is confirmed. If left empty, the default reservation
        confirmation text message will be used. If you add a custom message and
        check "Include link to view tickets", the link to view their tickets
        will be appended at the end of it. Confirmation emails are not affected.
      </p>
      <br />
      <ControlledFormSegmentedControl<EventFormData, GuestEmailRequirement>
        control={control}
        name="guestEmailRequirement"
        label="Guest email at checkout"
        options={GUEST_EMAIL_REQUIREMENT_OPTIONS}
        describedById={GUEST_EMAIL_HELPER_ID}
      />
      <p
        aria-live="polite"
        className={styles.description}
        id={GUEST_EMAIL_HELPER_ID}
      >
        {GUEST_EMAIL_REQUIREMENT_HELPER_TEXT[guestEmailRequirement]}
      </p>
    </section>
  );
};
